import React, { useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { formatInTitleCase } from "../../../../../../util/stringUtils";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

type EdgesPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues?: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const hasVanishingEdgeFieldName = "hasVanishingEdge";
const copingToVanishingEdgeFieldName = "copingToVanishingEdge";
const vanishingEdgeWidthFieldName = "vanishingEdgeWidth";

const catchBasinCoveredFieldName = "catchBasinCovered";
const separateBasinCoverFieldName = "separateCatchBasinCover";
const wallToCatchBasinHeightFieldName = "wallToCatchBasinHeight";
const separateBasinCoverRadios = [
  { label: "Yes (best for safety)", value: "yes" },
  { label: "Attached to main (if applicable)", value: "no" },
];

const slideOrRockBelowCopingFieldName = "slideOrRockBelowCoping";
const slideOrRockBelowCopingHeightFieldName = "slideOrRockBelowCopingHeight";

const hasSunLedgeFieldName = "hasSunLedge";

export default function EdgesPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: EdgesPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  //---------vanishing edge group state
  const hasVanishingEdgeAnswer =
    Form.useWatch(hasVanishingEdgeFieldName, { form, preserve: true }) ??
    initialFormValues?.[hasVanishingEdgeFieldName];
  const copingToVanishingEdge =
    Form.useWatch(copingToVanishingEdgeFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[copingToVanishingEdgeFieldName];
  const vanishingEdgeWidth =
    Form.useWatch(vanishingEdgeWidthFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[vanishingEdgeWidthFieldName];
  const hasVanishingEdge = hasVanishingEdgeAnswer === "yes";

  //---------basin group state
  const catchBasinCoveredAnswer =
    Form.useWatch(catchBasinCoveredFieldName, { form, preserve: true }) ??
    initialFormValues?.[catchBasinCoveredFieldName];
  const separateBasinCoverAnswer =
    Form.useWatch(separateBasinCoverFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[separateBasinCoverFieldName];
  const wallToCatchBasinHeight =
    Form.useWatch(wallToCatchBasinHeightFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[wallToCatchBasinHeightFieldName];
  const catchBasinCovered = catchBasinCoveredAnswer === "yes";
  const seperateBasinCover = separateBasinCoverAnswer === "yes";

  //---------slide or rock group state
  const slideOrRockBelowCopingAnswer =
    Form.useWatch(slideOrRockBelowCopingFieldName, { form, preserve: true }) ??
    initialFormValues?.[slideOrRockBelowCopingFieldName];
  const slideOrRockBelowCopingHeight =
    Form.useWatch(slideOrRockBelowCopingHeightFieldName, {
      form: form,
      preserve: true,
    }) ?? initialFormValues?.[slideOrRockBelowCopingHeightFieldName];
  const slideOrRockBelowCoping = slideOrRockBelowCopingAnswer === "yes";

  //---------sun ledge group state
  const hasSunLedgeAnswer =
    Form.useWatch(hasSunLedgeFieldName, { form, preserve: true }) ??
    initialFormValues?.[hasSunLedgeFieldName];

  return (
    <WizardPageWrapper
      title="Edges"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Vanishing Edge"
          closeButtonText={
            formGroupTouched.vanishingEdgeGroup ? "Save" : "Next"
          }
          onGroupClosed={() =>
            setFormGroupTouched({
              ...formGroupTouched,
              vanishingEdgeGroup: true,
            })
          }
          notExpandedDisplayValue={
            hasVanishingEdge
              ? `Yes ${copingToVanishingEdge} ${vanishingEdgeWidth}`
              : "No"
          }
          expandedOnMount={!formGroupTouched.vanishingEdgeGroup}
        >
          <Flex vertical gap="1rem">
            <Flex vertical gap="0.5rem">
              <Typography.Text>
                Does the pool have a vanishing edge?
              </Typography.Text>
              <Radio.Group
                options={yesOrNoRadios}
                value={hasVanishingEdgeAnswer}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (newValue === "no") {
                    return form.setFieldsValue({
                      [hasVanishingEdgeFieldName]: newValue,
                      [copingToVanishingEdgeFieldName]: "",
                      [vanishingEdgeWidthFieldName]: "",
                    });
                  }
                  form.setFieldValue(hasVanishingEdgeFieldName, newValue);
                }}
              />
            </Flex>
            {hasVanishingEdge && (
              <>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={copingToVanishingEdgeFieldName}
                  label="Vanishing Edge Width"
                >
                  <Input style={{ maxWidth: "330px", width: "100%" }} />
                </Form.Item>
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={vanishingEdgeWidthFieldName}
                  label="Distance from top of coping to vanishing edge wall"
                >
                  <Input style={{ maxWidth: "330px", width: "100%" }} />
                </Form.Item>
              </>
            )}
          </Flex>
        </FormGroupItem>
        {formGroupTouched.vanishingEdgeGroup && (
          <FormGroupItem
            label="Catch Basin Covered?"
            closeButtonText={formGroupTouched.basinGroup ? "Save" : "Next"}
            onGroupClosed={() =>
              setFormGroupTouched({ ...formGroupTouched, basinGroup: true })
            }
            notExpandedDisplayValue={
              catchBasinCovered
                ? `${formatInTitleCase(catchBasinCoveredAnswer)} ${
                    separateBasinCoverAnswer === "yes"
                      ? "Separate Basin Cover"
                      : "Attached Basin Cover"
                  }`
                : "No"
            }
            expandedOnMount={!formGroupTouched.basinGroup}
          >
            <Flex vertical gap="1rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Is the catch basin to be covered?
                </Typography.Text>
                <Radio.Group
                  options={yesOrNoRadios}
                  value={catchBasinCoveredAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [catchBasinCoveredFieldName]: newValue,
                        [separateBasinCoverFieldName]: "yes",
                        [wallToCatchBasinHeightFieldName]: "",
                      });
                    }
                    form.setFieldValue(catchBasinCoveredFieldName, newValue);
                  }}
                />
              </Flex>
              {catchBasinCovered && (
                <>
                  <Flex vertical gap="0.5rem">
                    <Typography.Text>Separate basin cover?</Typography.Text>
                    <Radio.Group
                      options={separateBasinCoverRadios}
                      value={separateBasinCoverAnswer}
                      onChange={(event) => {
                        const newValue = event.target.value;
                        if (newValue === "no") {
                          return form.setFieldsValue({
                            [separateBasinCoverFieldName]: newValue,
                            [wallToCatchBasinHeightFieldName]: "",
                          });
                        }
                        form.setFieldValue(
                          separateBasinCoverFieldName,
                          newValue
                        );
                      }}
                    />
                  </Flex>
                  {!seperateBasinCover && (
                    <Form.Item<CoverWizardForm>
                      style={{ margin: "0" }}
                      name={wallToCatchBasinHeightFieldName}
                      label="If attached, height from top of wall to catch basin"
                    >
                      <Input style={{ maxWidth: "330px", width: "100%" }} />
                    </Form.Item>
                  )}
                </>
              )}
            </Flex>
          </FormGroupItem>
        )}
        {formGroupTouched.basinGroup && (
          <FormGroupItem
            label="Slide or Rock"
            closeButtonText={
              formGroupTouched.slideOrRockGroup ? "Save" : "Next"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                slideOrRockGroup: true,
              })
            }
            notExpandedDisplayValue={
              slideOrRockBelowCoping
                ? `${formatInTitleCase(
                    slideOrRockBelowCopingAnswer
                  )} ${slideOrRockBelowCopingHeight}`
                : "No"
            }
            expandedOnMount={!formGroupTouched.slideOrRockGroup}
          >
            <Flex vertical gap="1rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Is there a slide or rock which dips below the coping level?
                </Typography.Text>
                <Radio.Group
                  options={yesOrNoRadios}
                  value={slideOrRockBelowCopingAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [slideOrRockBelowCopingFieldName]: newValue,
                        [slideOrRockBelowCopingHeightFieldName]: "",
                      });
                    }
                    form.setFieldValue(
                      slideOrRockBelowCopingFieldName,
                      newValue
                    );
                  }}
                />
              </Flex>
              {slideOrRockBelowCoping && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={slideOrRockBelowCopingHeightFieldName}
                  label="Location and height"
                >
                  <Input style={{ maxWidth: "282px", width: "100%" }} />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
        {formGroupTouched.slideOrRockGroup && (
          <FormGroupItem
            label="Sun Ledge"
            closeButtonText="Save"
            notExpandedDisplayValue={formatInTitleCase(hasSunLedgeAnswer)}
            expandedOnMount={!formGroupTouched.sunLedgeGroup}
            onGroupClosed={() =>
              setFormGroupTouched({ ...formGroupTouched, sunLedgeGroup: true })
            }
          >
            <Flex vertical gap="0.4rem">
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={hasSunLedgeFieldName}
                label="Does the pool have a sun ledge?"
              >
                <Radio.Group options={yesOrNoRadios} />
              </Form.Item>
              <Typography.Text>
                *If yes, include photos, plus measurements and location on the
                pool sketch.
              </Typography.Text>
            </Flex>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
